/**
 * What the checklist knows about its rows that the API deliberately does not: where each
 * one is completed, and in what order the groups read.
 *
 * The server judges every row from what the firm actually has and says which group it
 * belongs to; the words and the routes are this app's, because a route is a fact about the
 * console and the API has no business knowing the console has a Prices page.
 */

import type { SetupGroup, SetupItem, SetupState } from '../api';

/** The screen that completes each item. A key the server adds and this map lacks opens Setup. */
const ROUTES: Record<string, string> = {
  tariff: '/settings/prices',
  dispatcher: '/settings/fleet',
  driver: '/settings/fleet',
  vehicle: '/settings/fleet',
  logo: '/settings/branding',
  lines: '/settings/branding',
  about: '/settings/branding',
  legal: '/settings/branding',
  mapHome: '/settings/map',
  zones: '/settings/zones',
  addressSearch: '/settings/search',
  autoDispatch: '/settings/dispatch',
  surcharges: '/settings/surcharges',
  duty: '/settings/duty',
  fees: '/settings/prices',
  customers: '/settings/import',
  telephony: '/settings/telephony',
};

export function routeFor(key: string): string {
  return ROUTES[key] ?? '/settings/setup';
}

/** The groups in the order the page reads them, which is the order the server sends rows in. */
export const GROUP_ORDER: readonly SetupGroup[] = [
  'beforeFirstBooking',
  'face',
  'city',
  'money',
  'customers',
  'withUs',
];

/** The rows, grouped, in page order. A group with no rows for this firm is not listed. */
export function grouped(items: SetupItem[]): { group: SetupGroup; items: SetupItem[] }[] {
  return GROUP_ORDER.map((group) => ({
    group,
    items: items.filter((item) => item.group === group),
  })).filter((entry) => entry.items.length > 0);
}

/** The counted rows still open, in page order — what somebody stuck at 35% should do next. */
export function openItems(state: SetupState): SetupItem[] {
  return state.items.filter(
    (item) => item.counted && (item.state === 'Todo' || item.state === 'InProgress'),
  );
}

/** Whether the guide should open on arrival: only while nobody has ever seen it. */
export function shouldOpenGuide(state: SetupState): boolean {
  return state.guide.stage === 'NotStarted';
}

/**
 * Where a resumed guide picks up.
 *
 * The first screen for a guide never started or already finished — starting again is a
 * deliberate act from the Setup page and should begin at the beginning — and the screen
 * that was left, clamped to the five that exist, for one in progress.
 */
export function resumeStep(state: SetupState, steps: number): number {
  if (state.guide.stage !== 'InProgress') return 1;

  return Math.min(Math.max(state.guide.step, 1), steps);
}
